import {
    confirmResetPassword,
    confirmSignUp,
    fetchAuthSession,
    resendSignUpCode,
    resetPassword,
    signIn,
    signInWithRedirect,
    signOut,
    signUp,
} from 'aws-amplify/auth'
import { Hub } from 'aws-amplify/utils'
import eventBus from '@/service/EventBus.js'
import { isEqual, toLower } from 'lodash-es'
import { SystemPrefersDark } from '@/service/uiUtils.js'
import coachGreetService from '@/service/coachGreetService.js'
import { Capacitor } from '@capacitor/core'
import { CapacitorUpdater } from '@capgo/capacitor-updater'
import { FirebaseAppCheck } from '@capacitor-firebase/app-check'
import { ReCaptchaEnterpriseProvider } from 'firebase/app-check'
import { getApps, initializeApp } from 'firebase/app'
import api from '@/service/Api.js'
import { Badge } from '@capawesome/capacitor-badge'

Hub.listen('auth', async data => {
    if (data.payload.event === 'tokenRefresh') {
        console.log('tokenRefresh event')
        const { session, token, userId } = await getUserSession()
        eventBus.spa.emit('user-auth', { session, token, userId })
    }
})

// the first session after federated signUp won't have the userId, so allow the API to set it
let _userId = null

function setUserId({ userId }) {
    if (!isEqual(userId, _userId)) {
        setTimeout(async () => {
            const { session, token } = await getUserSession()
            eventBus.spa.emit('user-auth', { session, token, userId })
            if (userId) {
                heap.identify(userId)
                mixpanel.identify(userId)
            }
        }, 10)
    }
    _userId = userId
}

function getUserId() {
    return _userId
}

async function getUserSession() {
    const session = await fetchAuthSession()
    const token = session?.tokens?.idToken?.toString()
    const userId = session?.tokens?.idToken?.payload['kinectinUserId'] || _userId
    setUserId({ userId: _userId })
    return { session, token, userId }
}

/**
 * @returns {Promise<{ userId:string, email: string, username: string, deviceToken: string}>}
 */
async function getCurrentUser() {
    const { session } = await getUserSession()
    const username = session?.tokens?.idToken?.payload[`cognito:username`]
    const userId = session?.tokens?.idToken?.payload['kinectinUserId'] || _userId
    const email = session?.tokens?.idToken?.payload['email']
    setUserId({ userId: _userId })
    return { username, userId, email, deviceToken }
}

let _appCheckInitialized = false
let _appCheckInitializePromise = null

async function initializeRecaptcha() {
    if (_appCheckInitialized) return
    if (_appCheckInitializePromise) return _appCheckInitializePromise
    _appCheckInitializePromise = doInitializeRecaptcha()
    try {
        await _appCheckInitializePromise
        _appCheckInitialized = true
    } finally {
        _appCheckInitializePromise = null
    }
}

async function doInitializeRecaptcha() {
    let provider
    if (Capacitor.getPlatform() === 'web') {
        const firebaseConfig = JSON.parse(process.env.FIREBASE_WEB_CONFIG_JSON)
        if (!getApps().length) initializeApp(firebaseConfig)
        provider = new ReCaptchaEnterpriseProvider(process.env.GOOGLE_KEY_ID)
    }
    await FirebaseAppCheck.initialize({ isTokenAutoRefreshEnabled: false, provider })
    // await FirebaseAppCheck.initialize({ isTokenAutoRefreshEnabled: false, provider, debugToken: true })
}

async function getClientMetadata() {
    await initializeRecaptcha()
    const { token } = await FirebaseAppCheck.getToken({ forceRefresh: false }) // reCAPTCHA token
    return { token }
}

async function doSignUp({ email, password }) {
    const options = {
        userAttributes: { email: toLower(email) },
        clientMetadata: await getClientMetadata(),
    }
    await signUp({ username: toLower(email), password, options })
}

async function doResendSignUpCode({ email }) {
    const options = { clientMetadata: await getClientMetadata() }
    await resendSignUpCode({ username: toLower(email), options })
}

async function doConfirmSignUp({ email, confirmationCode }) {
    await confirmSignUp({ username: toLower(email), confirmationCode })
}

async function doResetPassword({ email }) {
    const options = { clientMetadata: await getClientMetadata() }
    await resetPassword({ username: toLower(email), options })
}

async function doConfirmResetPassword({ email, newPassword, confirmationCode }) {
    await confirmResetPassword({ username: toLower(email), newPassword, confirmationCode })
}

async function doSignIn({ email, password }) {
    await signIn({ username: toLower(email), password })
    const { session, token, userId } = await getUserSession()
    eventBus.spa.emit('user-auth', { session, token, userId })
}

async function doFederatedSignIn({ provider }) {
    if (Capacitor.isNativePlatform()) {
        // otherwise, the callback from Google will error out
        // because the capgo updater steals the network
        await CapacitorUpdater.setMultiDelay({
            delayConditions: [{ kind: 'date', value: '2075-01-01T00:00:00.000Z' }],
        })
    }
    await signInWithRedirect({ provider })
}

async function doFederatedCallback() {
    const { session, token, userId } = await getUserSession()
    eventBus.spa.emit('user-auth', { session, token, userId, deviceToken })
}

async function doSignOut({ noReload } = {}) {
    if (Capacitor.isNativePlatform()) {
        await Badge.set({ count: 0 })
        const { token } = await getUserSession()
        if (token && deviceToken) await api.logoutNative({ deviceToken })
        deviceToken = null
    }
    await coachGreetService.clear()
    _userId = null
    heap.resetIdentity()
    mixpanel.reset()
    await signOut()
    if (!noReload) window.location.href = '/'
}

eventBus.spa.on('device-token', handleDeviceToken)
let deviceToken = null

async function handleDeviceToken(token) {
    deviceToken = token
}

function getThemeForVuetify({ theme }) {
    if (['light', 'dark'].includes(theme)) return theme
    return SystemPrefersDark ? 'dark' : 'light'
}

export {
    getUserSession,
    doSignUp,
    doConfirmSignUp,
    doResendSignUpCode,
    doResetPassword,
    doConfirmResetPassword,
    doSignIn,
    doSignOut,
    getCurrentUser,
    getThemeForVuetify,
    doFederatedSignIn,
    doFederatedCallback,
    setUserId,
    getUserId,
    initializeRecaptcha,
}
